import { useId, useState, type ReactNode } from 'react';
import type { ChecksWithDate, DateList, Reminder } from '@tww/shared';
import { fmtDate, todayIso } from '@tww/shared';
import { Icon } from './Icon';
import { ComboInput, Dropdown, type DropdownOption } from './Dropdown';

interface BaseProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  full?: boolean;
  className?: string;
}

export function Field({ label, hint, error, required, full, className = '', children, htmlFor }: BaseProps & { children: ReactNode; htmlFor?: string }) {
  return (
    <div className={`field ${full ? 'full' : ''} ${error ? 'error' : ''} ${className}`}>
      {label && (
        <label htmlFor={htmlFor}>
          {label}
          {required && <span className="req"> *</span>}
        </label>
      )}
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

type InputProps = BaseProps & { value: string | null | undefined; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean; list?: string; autoFocus?: boolean; onEnter?: () => void };

export function TextInput({ value, onChange, placeholder, type = 'text', disabled, list, autoFocus, onEnter, ...f }: InputProps) {
  const id = useId();
  return (
    <Field {...f} htmlFor={id}>
      <input
        id={id}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        list={list}
        autoFocus={autoFocus}
        onKeyDown={onEnter ? (e) => e.key === 'Enter' && (e.preventDefault(), onEnter()) : undefined}
      />
    </Field>
  );
}

export function DateInput(p: Omit<InputProps, 'type'>) {
  return <TextInput {...p} type="date" />;
}

/** NumberInput mit deutscher Formatierung (Komma) */
/** Deutsche Zahleneingabe lesen: Komma ist Dezimaltrenner; ohne Komma gilt ein einzelner Punkt mit 1 bis 2 Nachkommastellen als Dezimalpunkt („0.9“), sonst als Tausenderpunkt („2.500“) */
export function parseDeNumber(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '');
  if (!t) return null;
  let s: string;
  if (t.includes(',')) s = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d+\.\d{1,2}$/.test(t)) s = t;
  else s = t.replace(/\./g, '');
  return /^-?\d*\.?\d+$/.test(s) ? Number(s) : NaN;
}

export function NumberInput({ value, onChange, placeholder, disabled, step, decimals, ...f }: BaseProps & { value: number | null | undefined; onChange: (v: number | null) => void; placeholder?: string; disabled?: boolean; step?: number; decimals?: number }) {
  const id = useId();
  const [invalid, setInvalid] = useState(false);
  return (
    <Field {...f} error={f.error ?? (invalid ? 'Bitte eine Zahl eingeben, z. B. 2,5' : undefined)} htmlFor={id}>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        defaultValue={value == null ? '' : (decimals != null ? Number(value).toFixed(decimals) : String(value)).replace('.', ',')}
        key={value == null ? 'empty' : String(value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={() => invalid && setInvalid(false)}
        onBlur={(e) => {
          const n = parseDeNumber(e.target.value);
          if (n === null) return (setInvalid(false), onChange(null));
          if (Number.isNaN(n)) return setInvalid(true);
          setInvalid(false);
          onChange(decimals != null ? Number(n.toFixed(decimals)) : step ? Math.round(n / step) * step : n);
        }}
      />
    </Field>
  );
}

export function TextArea({ value, onChange, rows = 3, placeholder, disabled, ...f }: BaseProps & { value: string | null | undefined; onChange: (v: string) => void; rows?: number; placeholder?: string; disabled?: boolean }) {
  const id = useId();
  return (
    <Field {...f} htmlFor={id} full={f.full ?? true}>
      <textarea id={id} rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} />
    </Field>
  );
}

export function Select({ value, onChange, options, placeholder = '–', disabled, clearable, searchable, ...f }: BaseProps & { value: string | null | undefined; onChange: (v: string) => void; options: DropdownOption[]; placeholder?: string; disabled?: boolean; clearable?: boolean; searchable?: boolean }) {
  const id = useId();
  return (
    <Field {...f} htmlFor={id}>
      <Dropdown id={id} value={value} onChange={onChange} options={options} placeholder={placeholder} disabled={disabled} clearable={clearable} searchable={searchable} aria-invalid={!!f.error} />
    </Field>
  );
}

/** Freitext mit Vorschlagsliste (ersetzt <datalist>) */
export function ComboField({ value, onChange, options, placeholder, disabled, newHint, emptyHint, ...f }: BaseProps & { value: string | null | undefined; onChange: (v: string) => void; options: DropdownOption[]; placeholder?: string; disabled?: boolean; newHint?: string; emptyHint?: ReactNode }) {
  const id = useId();
  return (
    <Field {...f} htmlFor={id}>
      <ComboInput id={id} value={value ?? ''} onChange={onChange} options={options} placeholder={placeholder} disabled={disabled} newHint={newHint} emptyHint={emptyHint} />
    </Field>
  );
}

export function MultiSelect({ value, onChange, options, disabled, ...f }: BaseProps & { value: string[]; onChange: (v: string[]) => void; options: { value: string; label: string }[]; disabled?: boolean }) {
  return (
    <Field {...f} full={f.full ?? true} label={f.label && options.length > 3 ? <>{f.label} <span className="check-count">{options.filter((o) => value.includes(o.value)).length} von {options.length}</span></> : f.label}>
      <ul className="check-list no-date">
        {options.map((o) => {
          const on = value.includes(o.value);
          return (
            <li key={o.value} className={on ? 'on' : ''}>
              <label>
                <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((x) => x !== o.value))} />
                <OptionLabel text={o.label} />
              </label>
            </li>
          );
        })}
      </ul>
    </Field>
  );
}
/** Zusatz in Klammern als zweite, gedämpfte Zeile: „Pflanzbestätigung (PDF, wird erzeugt)“ */
function OptionLabel({ text }: { text: string }) {
  const m = /^(.+?)\s*\(([^()]+)\)$/.exec(text);
  return m ? (
    <span>
      {m[1]}
      <span className="sub">{m[2]}</span>
    </span>
  ) : (
    <span>{text}</span>
  );
}
export function Checkbox({ value, onChange, label, hint, disabled }: { value: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean }) {
  return (
    <div className="field">
      <label className="radios" style={{ paddingTop: 0 }}>
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} disabled={disabled} /> {label}
      </label>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/** DateListInput: Mehrfachangabe "Angebot eingeholt am" */
export function DateListInput({ value, onChange, disabled, ...f }: BaseProps & { value: DateList; onChange: (v: DateList) => void; disabled?: boolean }) {
  return (
    <Field {...f}>
      <div className="dates">
        {value.map((d, i) => (
          <div className="drow" key={i}>
            <input type="date" value={d} disabled={disabled} onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
            {!disabled && (
              <button type="button" className="btn sm" onClick={() => onChange(value.filter((_, j) => j !== i))}>
                Entfernen
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button type="button" className="btn sm" onClick={() => onChange([...value, todayIso()])}>
            <Icon name="plus" /> Datum hinzufügen
          </button>
        )}
      </div>
    </Field>
  );
}

/** ChecksWithDate: eine Zeile je Option, ganze Zeile anklickbar; Anhaken setzt das heutige Datum, das rechts in der Zeile änderbar bleibt */
export function ChecksWithDateInput({ value, onChange, options, noDate, disabled, ...f }: BaseProps & { value: ChecksWithDate; onChange: (v: ChecksWithDate) => void; options: string[]; noDate?: boolean; disabled?: boolean }) {
  const count = options.filter((o) => value[o]).length;
  const toggle = (o: string, on: boolean) => {
    const n = { ...value };
    if (on) n[o] = noDate ? true : todayIso();
    else delete n[o];
    onChange(n);
  };
  return (
    <Field
      {...f}
      full={f.full ?? true}
      label={
        f.label && (
          <>
            {f.label} <span className="check-count">{count} von {options.length}</span>
          </>
        )
      }
    >
      <ul className={`check-list ${noDate ? 'no-date' : ''}`}>
        {options.map((o) => {
          const v = value[o];
          return (
            <li key={o} className={v ? 'on' : ''}>
              <label>
                <input type="checkbox" checked={Boolean(v)} disabled={disabled} onChange={(e) => toggle(o, e.target.checked)} />
                <span>{o}</span>
              </label>
              {v && !noDate && <input type="date" className="check-date" value={typeof v === 'string' ? v : todayIso()} disabled={disabled} onChange={(e) => onChange({ ...value, [o]: e.target.value || todayIso() })} aria-label={`Datum ${o}`} />}
            </li>
          );
        })}
      </ul>
    </Field>
  );
}

/** ReminderField: Datum plus Notiz, erscheint zum Datum auf der Übersicht */
export function ReminderField({ value, onChange, disabled, ...f }: BaseProps & { value: Reminder; onChange: (v: Reminder) => void; disabled?: boolean }) {
  return (
    <Field {...f} full={f.full ?? true} hint={f.hint ?? 'Erscheint zum Datum auf der Übersicht'}>
      <div className="form" style={{ gap: '8px 12px' }}>
        <input type="date" value={value.date ?? ''} disabled={disabled} onChange={(e) => onChange({ ...value, date: e.target.value || null })} aria-label="Erinnerungsdatum" />
        <input value={value.note} disabled={disabled} onChange={(e) => onChange({ ...value, note: e.target.value })} placeholder="Notiz zur Erinnerung" aria-label="Notiz" />
      </div>
      {value.date && <span className="pre">Erinnerung am {fmtDate(value.date)}</span>}
    </Field>
  );
}
